import { all, call, put, takeEvery } from 'redux-saga/effects'

import add from '../../rest/requests/answer/add'
import archiveAnswer from '../../rest/requests/answer/archive'
import deleteAnswer from '../../rest/requests/classifieds/deleteAnswer'
import getAnswers from '../../rest/requests/classifieds/getAnswers'
import GetContentTypeSchema from '../../rest/requests/content-types-builder/getContentTypeSchema'
import upload from '../../rest/requests/media/upload'
import tracking from '../../tracking'
import { ClassifiedCategoryId } from '../../types/classified'
import ApiSagas from '../api/sagas'
import { actions } from '../index'

import seenAnswer from 'src/rest/requests/classifieds/seenAnswer'
import {
  getStrapiUploadFirstFileId,
  strapiRelationConnect,
} from '../../helpers/strapiRest'
import { adsReplyFormTransformer } from './transformers'

export default class AnswerSaga {
  static *onAddAnswerRequest({
    payload,
  }: ReturnType<typeof actions.answer.addAnswerRequest>) {
    const transformed: ReturnType<typeof adsReplyFormTransformer> = yield call(
      adsReplyFormTransformer,
      { values: payload }
    )

    const data: any = { ...transformed.data }
    delete data.file
    delete data.uploadStudentCard

    if (transformed.files?.length) {
      for (const file of transformed.files) {
        const uploadResponse = yield* ApiSagas.rest(
          upload({ files: file.value })
        )
        const uploadedId = getStrapiUploadFirstFileId(uploadResponse?.data)
        if (!uploadedId) {
          yield put(
            actions.answer.addAnswerError(
              uploadResponse?.errors ?? [{ message: 'File upload failed' }]
            )
          )
          return
        }
        const path = file.name.replace(/^files\./, '').split('.')
        let cursor = data
        for (let i = 0; i < path.length - 1; i++) {
          const key = path[i]
          cursor[key] = { ...(cursor[key] ?? {}) }
          cursor = cursor[key]
        }
        cursor[path[path.length - 1]] = uploadedId
      }
    }

    if (data.classified != null && data.classified !== '') {
      data.classified = strapiRelationConnect(data.classified)
    }
    if (data.answer_user != null && data.answer_user !== '') {
      data.answer_user = strapiRelationConnect(data.answer_user)
    }

    const response = yield* ApiSagas.rest(add({ data }))

    if (response.data) {
      yield put(actions.answer.addAnswerSuccess(response.data))
      if (
        payload.fullClassified !== undefined &&
        payload.fullClassified.classifiedCategory !== undefined &&
        String(payload.fullClassified.classifiedCategory.id) !==
          ClassifiedCategoryId.Liberal
      ) {
        yield put(
          actions.chat.createNewChannel({
            message: payload.message,
            classifiedAuthor: payload.fullClassified,
            response: response.data,
          })
        )
        yield call(tracking.conversation_start, payload)
      }
    } else {
      yield put(actions.answer.addAnswerError(response.errors))
    }
  }

  static *onGetAnswerSchema() {
    const response = yield* ApiSagas.rest(
      GetContentTypeSchema({ uid: 'api::classified-answer.classified-answer' })
    )

    if (response.data) {
      yield put(actions.answer.getAnswerSchemaSuccess(response.data))
    } else {
      yield put(actions.answer.getAnswerSchemaReset(response.errors))
    }
  }

  static *onGetAnswers({
    payload,
  }: ReturnType<typeof actions.answer.getAnswersRequest>) {
    const response = yield* ApiSagas.rest(getAnswers(payload))

    if (!response.errors && response.data) {
      yield put(actions.answer.getAnswersSuccess(response.data))
    } else {
      yield put(actions.answer.getAnswersError(response.errors))
    }
  }

  static *onGetAnswersIds({
    payload,
  }: ReturnType<typeof actions.answer.getAnswersIdsRequest>) {
    const response = yield* ApiSagas.rest(getAnswers(payload))

    if (!response.errors && response.data) {
      yield put(actions.answer.getAnswersIdsSuccess(response.data))
    } else {
      yield put(actions.answer.getAnswersIdsError(response.errors))
    }
  }

  static *onDeleteAnswer({
    payload,
  }: ReturnType<typeof actions.answer.deleteAnswerRequest>) {
    const response = yield* ApiSagas.rest(deleteAnswer(payload))

    if (!response.errors && response.data) {
      yield put(actions.answer.deleteAnswerSuccess(response.data))
    } else {
      yield put(actions.answer.deleteAnswerError(response.errors))
    }
  }

  static *onArchiveAnswer({
    payload,
  }: ReturnType<typeof actions.answer.archiveAnswerRequest>) {
    const response = yield* ApiSagas.rest(archiveAnswer(payload))

    if (!response.errors && response.data) {
      yield put(actions.answer.archiveAnswerSuccess(response.data))
    } else {
      yield put(actions.answer.archiveAnswerError(response.errors))
    }
  }

  static *onSeenAnswer({
    payload,
  }: ReturnType<typeof actions.answer.seenAnswerRequest>) {
    const response = yield* ApiSagas.rest(seenAnswer(payload))

    if (!response.errors && response.data) {
      yield put(actions.answer.seenAnswerSuccess(response.data))
    } else {
      yield put(actions.answer.seenAnswerError(response.errors))
    }
  }

  static *loop() {
    yield all([
      takeEvery(actions.answer.seenAnswerRequest, this.onSeenAnswer),
      takeEvery(actions.answer.getAnswerSchemaRequest, this.onGetAnswerSchema),
      takeEvery(actions.answer.addAnswerRequest, this.onAddAnswerRequest),
      takeEvery(actions.answer.getAnswersRequest, this.onGetAnswers),
      takeEvery(actions.answer.getAnswersIdsRequest, this.onGetAnswersIds),
      takeEvery(actions.answer.deleteAnswerRequest, this.onDeleteAnswer),
      takeEvery(actions.answer.archiveAnswerRequest, this.onArchiveAnswer),
    ])
  }
}
